import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EnumService } from './enum.service';
import { DocumentViewerComponent } from '../../components/document-viewer/document-viewer.component';
import { MimeTypeName } from '../../constants/mime-types';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  private dialog = inject(MatDialog);
  private enumService = inject(EnumService);

  previewFile(response: Blob, fileName: string, openInNewWindow: boolean = false, jsonObject?: Record<string, unknown>): void {
    const blobUrl = response ? URL.createObjectURL(response) : null;
    const mimeType = response?.type ?? null;
    openInNewWindow ? this.openDocumentInWindow(blobUrl, fileName, mimeType) : this.openDocumentInPopup(blobUrl, mimeType, fileName, jsonObject);
  }

  openDocumentInWindow(blobUrl: string, fileName: string, mimeType: string): void {
    const features = this.getPopupWindowFeatures();
    const html = `<html>
                    <head><title>${fileName}</title></head>
                    <body style="margin:0"><embed src="${blobUrl}" width="100%" height="100%" type="${mimeType}"/></body>
                  </html>`;
    const newWindow = window.open('', '_blank', features);
    if (newWindow) {
      newWindow.document.open();
      newWindow.document.write(html);
      newWindow.document.close();
    }
  }

  openDocumentInPopup(blobUrl: string, mimeType: string, fileName: string, jsonObject?: Record<string, unknown>): void {
    this.dialog.open(DocumentViewerComponent, {
      panelClass: ['primary-modal', 'custom-dialog'],
      data: { blobUrl, mimeType, fileName, jsonObject },
      minWidth: '300px',
      minHeight: '200px',
      width: mimeType == MimeTypeName.pdf ? '' : 'fit-content',
    });
    this.dialog.afterAllClosed.subscribe(() => blobUrl && URL.revokeObjectURL(blobUrl));
  }

  downloadFileFromBlob<T>(response: Blob, attachement: T) {
    const url = window.URL.createObjectURL(response);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${attachement['attachmentName']}.${attachement['attachmentType']}`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  getFileTypesByMIMEType(mimeTypes: string[]): string | undefined {
    let fileTypes: string[] = undefined;
    if (mimeTypes && mimeTypes.length > 0) {
      fileTypes = [];
      mimeTypes.forEach(type => fileTypes.push(this.enumService.getEnumKeyByValue(MimeTypeName, type)));
      return fileTypes.join(', ');
    }
    return undefined;
  }

  getFileExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex !== -1 ? fileName.substring(dotIndex + 1).toLowerCase() : '';
  }

  printPdf = (res: string) => {
    const binary = atob(res);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++)
      array[i] = binary.charCodeAt(i);

    const blob = new Blob([array], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      window.URL.revokeObjectURL(blobUrl);
    };
  };

  downloadPdf = (base64String: string, fileName: string) => {
    const binary = atob(base64String);
    const array = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++)
      array[i] = binary.charCodeAt(i);

    const blob = new Blob([array], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  private getPopupWindowFeatures(): string {
    const options = {
      width: 800,
      height: 600,
      top: 100,
      left: 200,
      location: 'no',
      status: 'no',
      menubar: 'no',
      scrollbars: 'yes',
      resizable: 'yes',
    };

    return Object.entries(options)
      .map(([key, value]) => `${key}=${value}`)
      .join(',');
  }
}
